import { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { StatCard } from '@/components/admin/StatCard';
import { useAdminApi } from '@/hooks/useAdminApi';
import { Users, Clock, HardDrive, Sparkles, FolderKanban, StickyNote, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface Stats {
  totalUsers: number;
  waitlistCount: number;
  totalStorageMb: number;
  totalAiRequests: number;
  totalBoards: number;
  totalItems: number;
}

interface UserData {
  id: string;
  email: string;
  storage_used_mb: number;
  storage_limit_mb: number;
  ai_requests_used: number;
  ai_requests_limit: number;
  board_count: number;
  item_count: number;
  last_sign_in_at: string | null;
}

const CHART_COLORS = ['hsl(239, 84%, 67%)', 'hsl(160, 84%, 39%)', 'hsl(38, 92%, 50%)', 'hsl(350, 89%, 60%)', 'hsl(263, 70%, 50%)', 'hsl(187, 85%, 43%)'];

export default function AdminOverview() {
  const { getStats, getUsers, isLoading } = useAdminApi();
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<UserData[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, usersData] = await Promise.all([
          getStats(),
          getUsers()
        ]);
        setStats(statsData);
        setUsers(usersData as UserData[]);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setUsersLoading(false);
      }
    };
    fetchData();
  }, [getStats, getUsers]);

  const formatStorage = (mb: number) => {
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(1)} MB`;
  };

  const formatTimeAgo = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Calculate aggregated stats
  const totalStorageLimit = users.reduce((sum, u) => sum + (u.storage_limit_mb || 0), 0);
  const totalAILimit = users.reduce((sum, u) => sum + (u.ai_requests_limit || 0), 0);
  const storagePercent = totalStorageLimit > 0 ? ((stats?.totalStorageMb || 0) / totalStorageLimit) * 100 : 0;
  const aiPercent = totalAILimit > 0 ? ((stats?.totalAiRequests || 0) / totalAILimit) * 100 : 0;

  // Prepare chart data
  const storageByUserData = users
    .filter(u => u.storage_used_mb > 0)
    .sort((a, b) => b.storage_used_mb - a.storage_used_mb)
    .slice(0, 8)
    .map(u => ({
      name: u.email.split('@')[0],
      storage: u.storage_used_mb,
      fullEmail: u.email
    }));

  // Items by type data (mock - would need to fetch from items table)
  const itemsByTypeData = [
    { name: 'Sticky Notes', value: 35, color: CHART_COLORS[0] },
    { name: 'Images', value: 25, color: CHART_COLORS[1] },
    { name: 'Documents', value: 20, color: CHART_COLORS[2] },
    { name: 'Links', value: 15, color: CHART_COLORS[3] },
    { name: 'Other', value: 5, color: CHART_COLORS[4] },
  ];

  // Sort users by AI usage for table
  const sortedUsers = [...users].sort((a, b) => b.ai_requests_used - a.ai_requests_used);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <p className="text-muted-foreground">Platform metrics, usage analytics, and user management</p>
        </div>

        {/* Primary Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {isLoading || !stats ? (
            <>
              {[1, 2, 3, 4].map((i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-4 w-20 mb-2" />
                    <Skeleton className="h-8 w-16" />
                  </CardContent>
                </Card>
              ))}
            </>
          ) : (
            <>
              <StatCard
                title="Total Users"
                value={stats.totalUsers}
                icon={Users}
                trend={`+${stats.waitlistCount} on waitlist`}
              />
              <StatCard
                title="AI Requests"
                value={stats.totalAiRequests.toLocaleString()}
                icon={Sparkles}
                trend={`${aiPercent.toFixed(1)}% of capacity`}
              />
              <StatCard
                title="Storage Used"
                value={formatStorage(stats.totalStorageMb)}
                icon={HardDrive}
                trend={`of ${formatStorage(totalStorageLimit)}`}
              />
              <StatCard
                title="Total Items"
                value={stats.totalItems.toLocaleString()}
                icon={StickyNote}
                trend={`${stats.totalBoards} boards`}
              />
            </>
          )}
        </div>

        {/* Usage Bars */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                AI Usage Capacity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{stats?.totalAiRequests.toLocaleString() || 0} requests used</span>
                  <span className="text-muted-foreground">{totalAILimit.toLocaleString()} total</span>
                </div>
                <Progress value={aiPercent} className="h-3" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <HardDrive className="h-4 w-4" />
                Storage Capacity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{formatStorage(stats?.totalStorageMb || 0)} used</span>
                  <span className="text-muted-foreground">{formatStorage(totalStorageLimit)} total</span>
                </div>
                <Progress value={storagePercent} className="h-3" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Storage by User Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Storage by User
              </CardTitle>
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <Skeleton className="h-64 w-full" />
              ) : storageByUserData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={storageByUserData} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <XAxis type="number" tickFormatter={(v) => `${v.toFixed(0)}MB`} fontSize={12} />
                    <YAxis type="category" dataKey="name" width={80} fontSize={12} />
                    <Tooltip 
                      formatter={(value: number) => [`${value.toFixed(2)} MB`, 'Storage']}
                      labelFormatter={(label, payload) => payload?.[0]?.payload?.fullEmail || label}
                    />
                    <Bar dataKey="storage" fill="hsl(239, 84%, 67%)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No storage data yet
                </div>
              )}
            </CardContent>
          </Card>

          {/* Items by Type Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <StickyNote className="h-5 w-5" />
                Items by Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <Skeleton className="h-64 w-full" />
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={itemsByTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {itemsByTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              All Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            {usersLoading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-right">AI Used</TableHead>
                      <TableHead className="text-right">Storage</TableHead>
                      <TableHead className="text-right">Items</TableHead>
                      <TableHead className="text-right">Last Active</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedUsers.map((user) => {
                      const userAiPercent = (user.ai_requests_used / (user.ai_requests_limit || 1)) * 100;
                      const userStoragePercent = (user.storage_used_mb / (user.storage_limit_mb || 1)) * 100;
                      
                      return (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">
                            <span className="truncate max-w-[200px] inline-block" title={user.email}>
                              {user.email}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <span className={userAiPercent > 80 ? 'text-destructive font-medium' : ''}>
                                {user.ai_requests_used}/{user.ai_requests_limit}
                              </span>
                              <Progress value={userAiPercent} className="w-16 h-2" />
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <span className={userStoragePercent > 80 ? 'text-destructive font-medium' : ''}>
                                {formatStorage(user.storage_used_mb)}
                              </span>
                              <Progress value={userStoragePercent} className="w-16 h-2" />
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {user.item_count}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {formatTimeAgo(user.last_sign_in_at)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a
                href="/admin/users"
                className="p-4 border rounded-lg hover:bg-muted transition-colors text-center"
              >
                <Users className="w-8 h-8 mx-auto mb-2 text-primary" />
                <p className="font-medium">Manage Users</p>
                <p className="text-sm text-muted-foreground">View, create, edit users</p>
              </a>
              <a
                href="/admin/waitlist"
                className="p-4 border rounded-lg hover:bg-muted transition-colors text-center"
              >
                <Clock className="w-8 h-8 mx-auto mb-2 text-primary" />
                <p className="font-medium">View Waitlist</p>
                <p className="text-sm text-muted-foreground">Invite waitlist users</p>
              </a>
              <a
                href="/admin/storage"
                className="p-4 border rounded-lg hover:bg-muted transition-colors text-center"
              >
                <HardDrive className="w-8 h-8 mx-auto mb-2 text-primary" />
                <p className="font-medium">Storage Analytics</p>
                <p className="text-sm text-muted-foreground">Monitor storage usage</p>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
